// alarmNotificationContext.js
// Cola entre a escuta única, a máquina de estados e a camada de áudio.
//
// Este módulo não contém regra de alarme: ele apenas alimenta a máquina com os
// frames que chegam, adianta o relógio dela e reflete o resultado em áudio e
// avisos. A regra vive em alarmStateMachine.js, que é puro e testado à parte.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useToken } from "./tokenContext";
import useApi from "./apiService";
import { useWebSocketChannel } from "./websocketContext";
import {
  createState,
  applyFrame,
  tick,
  dismiss,
  setContext,
  seedSnapshot,
  seedNotices,
} from "./alarmStateMachine";
import { loadSnapshot, saveSnapshot, clearSnapshot } from "./alarmSnapshot";
import {
  loadAudioPreference,
  saveAudioPreference,
} from "./alarmAudioPreference";
import { createAlarmAudio, AUDIO_BLOCKED } from "./alarmAudio";

// Avisos visíveis ao mesmo tempo; os excedentes ficam em fila e entram conforme
// os visíveis se encerram. Nenhum aviso é descartado por falta de espaço: a
// máquina de estados retém todos, e o corte é só de apresentação.
export const MAX_VISIBLE_NOTICES = 5;

// Resolução do relógio da janela de confirmação. Bem abaixo de 1 s para que o
// aviso apareça dentro do limite de latência exigido no escopo.
export const TICK_INTERVAL_MS = 250;

// O grupo de visualização pode ser trocado pela interface em tempo de execução
// (modalFilter reescreve o usuário no localStorage). Sem reagir a isso, o
// operador seguiria filtrado pelo contexto antigo até recarregar a página.
export const CONTEXT_POLL_MS = 2000;

// Tetos do portão de semeadura. O portão retém frames até o catálogo chegar,
// mas NUNCA pode reter para sempre: o serviço de API não define timeout, então
// um back-end pendurado deixaria a notificação muda em silêncio absoluto —
// justamente o desfecho que esta feature existe para evitar. Estourado o teto,
// o portão abre e os frames passam a ser aplicados sem retrato semeado, o que
// degrada a precisão mas mantém o alarme vivo.
export const SEED_TIMEOUT_MS = 5000;
export const MAX_PENDING_FRAMES = 2000;

// Cadência da reconciliação do reconhecimento entre sessões. Só roda enquanto
// houver aviso ativo — sem alarme na tela, nenhuma consulta é feita.
export const RECOGNITION_POLL_MS = 10000;

const AlarmNotificationContext = createContext(null);

export const useAlarmNotifications = () => useContext(AlarmNotificationContext);

const readUser = () => {
  try {
    return JSON.parse(window.localStorage.getItem("user"));
  } catch {
    return null;
  }
};

// O nível corrente do ativo vem no último frame guardado pelo back-end.
const levelFromLastSample = (lastSample) => {
  try {
    return JSON.parse(lastSample)?.alarmed;
  } catch {
    return undefined;
  }
};

/**
 * Labels dos ativos com pelo menos um alarme aberto e não reconhecido.
 *
 * Recebe o resultado de `Promise.allSettled` porque a lista de alarmes é
 * opcional: sem ela, nenhum aviso é reaberto — o operador volta a depender da
 * transição, que é a degradação aceitável. Um formato inesperado tem de cair
 * no mesmo lugar, e não derrubar a semeadura inteira.
 */
const naoReconhecidosPorAtivo = (resultado) => {
  if (!resultado || resultado.status !== "fulfilled") return [];
  const alarmes = resultado.value?.data;
  if (!Array.isArray(alarmes)) return [];
  const labels = alarmes
    .filter((a) => a && !a.end_time && !a.recognized && a.device?.label)
    .map((a) => a.device.label);
  return [...new Set(labels)];
};

export const AlarmNotificationProvider = ({
  children,
  audioFactory = createAlarmAudio,
}) => {
  const { token } = useToken();
  const { fetchData } = useApi();
  const { subscribe, connected } = useWebSocketChannel();

  // `useApi` devolve um `fetchData` novo a cada render, fechando sobre o token
  // DAQUELE render. Capturá-lo num useCallback com deps vazias congelaria o
  // token da tela de login (nulo), e toda chamada feita por ele responderia
  // 401 — o contexto do operador nunca resolveria.
  const fetchRef = useRef(fetchData);
  useEffect(() => {
    fetchRef.current = fetchData;
  });

  const [audio] = useState(() => audioFactory());
  // O retrato é escopado por operador. O escopo NÃO pode ser congelado na
  // montagem: este provider vive no shell e é montado antes do login, quando
  // ainda não há usuário no localStorage.
  const scopeRef = useRef(readUser()?.user?.id);
  const [machine, setMachine] = useState(() =>
    createState({ snapshot: loadSnapshot(scopeRef.current) }),
  );
  // ToSenseID -> { id, name }. O modal de reconhecimento exige o UUID do ativo,
  // que o canal de tempo real não carrega — só o label.
  const [devices, setDevices] = useState({});
  const [audioStatus, setAudioStatus] = useState(AUDIO_BLOCKED);
  const [audioVolume, setAudioVolume] = useState(() => audio.getVolume());
  const [contextResolved, setContextResolved] = useState(true);
  // Avisos recolhidos pelo operador. Vive aqui, e não no componente, para que
  // recolher sobreviva a remontagens da árvore de apresentação.
  const [minimizados, setMinimizados] = useState([]);

  // Portão de semeadura: enquanto /devices não responder, os frames que chegam
  // ficam retidos. Aplicá-los antes da carga inicial faria o primeiro frame de
  // um ativo virar o retrato dele — e um ativo que entrasse em crítico nessa
  // janela seria lido como "já estava crítico na abertura", nunca notificado.
  const seededRef = useRef(false);
  const pendingRef = useRef([]);
  const liberarPortaoRef = useRef(null);
  // Portão aberto sem retrato semeado é o único estado degradado que restava
  // invisível. Nele o primeiro frame de cada ativo vira o retrato dele, e um
  // ativo que entre em crítico é lido como "já estava crítico na abertura" —
  // ou seja, deixa de notificar. A premissa da feature é que degradação
  // silenciosa é o pior desfecho, então este estado também é anunciado.
  const [snapshotSeeded, setSnapshotSeeded] = useState(true);

  const resolveContext = useCallback(async () => {
    const user = readUser();

    if (!user) {
      // Não foi possível ler a sessão: não dá para afirmar qual é o contexto.
      setContextResolved(false);
      setMachine((m) => setContext(m, null));
      return;
    }

    const viewGroup = user.user?.view_group;
    if (!viewGroup) {
      // Operador sem grupo de visualização enxerga todos os ativos — isso é um
      // contexto resolvido, não uma falha.
      setContextResolved(true);
      setMachine((m) => setContext(m, null));
      return;
    }

    try {
      const relations = await fetchRef.current(
        `view/groups/${viewGroup.id}/list-relations`,
      );
      setContextResolved(true);
      setMachine((m) =>
        setContext(
          m,
          relations.data.map((relation) => relation.device.label),
        ),
      );
    } catch {
      // Falha ao resolver o contexto não pode silenciar alarme: sem a lista, a
      // notificação segue sem restrição. O risco é avisar de ativo alheio,
      // preferível a deixar de avisar de um crítico do próprio operador. O
      // operador é informado disso pelo indicador (contextResolved).
      setContextResolved(false);
    }
    // Deps vazias SEM silenciar o linter: tudo aqui é ref ou setter estável.
    // A combinação anterior (deps vazias + eslint-disable) foi o que congelou o
    // fetchData da tela de login; deixar o linter falar impede que a próxima
    // leitura de prop ou estado aqui dentro volte a congelar em silêncio.
  }, []);

  // Troca de operador na mesma aba: o retrato do anterior não pode ser herdado.
  useEffect(() => {
    const atual = readUser()?.user?.id;
    if (atual === scopeRef.current) return;
    // O retrato do operador anterior não tem mais dono nesta aba.
    if (scopeRef.current !== undefined) clearSnapshot(scopeRef.current);
    scopeRef.current = atual;
    pendingRef.current = [];
    // Só refecha o portão quando há sessão para semeá-lo. No logout o efeito de
    // carga retorna antes de armar o teto, e deixar o portão fechado ali criaria
    // um estado sem nenhuma das duas redes de segurança.
    seededRef.current = atual === undefined ? true : false;
    setMachine(createState({ snapshot: loadSnapshot(atual) }));
  }, [token]);

  // Catálogo de ativos, retrato inicial e contexto do operador.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    // O buffer precisa ser capturado ANTES de qualquer setMachine: o updater do
    // React roda depois do bloco síncrono, e limpar `pendingRef` em seguida
    // faria a reprodução encontrar uma lista já vazia.
    const drenar = () => {
      const retidos = pendingRef.current;
      pendingRef.current = [];
      seededRef.current = true;
      return retidos;
    };

    const abrirPortaoSemSemeadura = () => {
      if (cancelled || seededRef.current) return;
      setSnapshotSeeded(false);
      const retidos = drenar();
      setMachine((m) =>
        retidos.reduce((acc, { frame, at }) => applyFrame(acc, frame, at), m),
      );
    };

    // Rede de segurança do portão: se o catálogo não chegar a tempo, os frames
    // passam a fluir mesmo sem retrato semeado.
    const teto = setTimeout(abrirPortaoSemSemeadura, SEED_TIMEOUT_MS);
    liberarPortaoRef.current = abrirPortaoSemSemeadura;

    const load = async () => {
      try {
        // As duas chamadas correm em paralelo: a lista de alarmes é usada para
        // reabrir avisos de crítico não reconhecido, e serializá-la atrasaria a
        // abertura do portão pelo tempo de mais uma ida ao servidor.
        //
        // `allSettled` e não `all`: a lista de alarmes é um complemento. Se ela
        // falhar, a semeadura do retrato — que é o que impede aviso falso — não
        // pode falhar junto.
        const [catalogo, listaAlarmes] = await Promise.allSettled([
          fetchRef.current("/devices"),
          fetchRef.current("/alarms"),
        ]);
        if (cancelled) return;
        if (catalogo.status !== "fulfilled") throw catalogo.reason;
        const response = catalogo.value;

        setDevices(
          Object.fromEntries(
            response.data.map((device) => [
              device.label,
              { id: device.id, name: device.name },
            ]),
          ),
        );

        const levels = Object.fromEntries(
          response.data
            .map((device) => [
              device.label,
              levelFromLastSample(device.last_sample),
            ])
            .filter(([, level]) => level !== undefined),
        );

        // Ativos com alarme ABERTO e ainda NÃO reconhecido por ninguém. Só
        // estes reabrem aviso na abertura da sessão; o que alguém já tratou
        // fica calado, senão todo F5 repetiria o alerta já resolvido.
        const naoReconhecidos = naoReconhecidosPorAtivo(listaAlarmes);

        // Semeia e só então reproduz, em ordem, os frames retidos. É isto que
        // fecha a janela entre o socket abrir e o catálogo responder.
        //
        // `seedNotices` vem DEPOIS de `seedSnapshot` (precisa do retrato para
        // saber quem está crítico) e ANTES da reprodução, para que um frame
        // retido que já normalizou o ativo encerre o aviso reaberto.
        const retidos = drenar();
        setSnapshotSeeded(true);
        setMachine((m) =>
          retidos.reduce(
            (acc, { frame, at }) => applyFrame(acc, frame, at),
            seedNotices(seedSnapshot(m, levels), naoReconhecidos, Date.now()),
          ),
        );
      } catch {
        // Sem catálogo o aviso ainda aparece, identificado pelo label do ativo.
        // Os frames retidos precisam ser liberados de qualquer forma.
        if (!cancelled) {
          setSnapshotSeeded(false);
          const retidos = drenar();
          setMachine((m) =>
            retidos.reduce(
              (acc, { frame, at }) => applyFrame(acc, frame, at),
              m,
            ),
          );
        }
      }

      if (!cancelled) await resolveContext();
    };

    load();
    return () => {
      cancelled = true;
      clearTimeout(teto);
      liberarPortaoRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Troca do grupo de visualização em tempo de execução.
  useEffect(() => {
    if (!token) return undefined;
    let anterior = readUser()?.user?.view_group?.id ?? null;
    const id = setInterval(() => {
      const atual = readUser()?.user?.view_group?.id ?? null;
      if (atual === anterior) return;
      anterior = atual;
      resolveContext();
    }, CONTEXT_POLL_MS);
    return () => clearInterval(id);
  }, [token, resolveContext]);

  // Assinatura SÍNCRONA do canal. Precisa ser `subscribe` e não `messages`:
  // frames que chegam no mesmo tick do event loop são coalescidos pelo estado
  // React, e um frame engolido é uma transição para crítico que nunca notifica.
  useEffect(
    () =>
      subscribe((frame) => {
        const at = Date.now();
        if (!seededRef.current) {
          pendingRef.current.push({ frame, at });
          // Buffer cheio significa que o catálogo demorou demais. Abrir o
          // portão é preferível a acumular memória enquanto o operador fica
          // sem alarme.
          if (pendingRef.current.length >= MAX_PENDING_FRAMES) {
            liberarPortaoRef.current?.();
          }
          return;
        }
        setMachine((m) => applyFrame(m, frame, at));
      }),
    [subscribe],
  );

  // Relógio da janela de confirmação. Reaproveitado para reler o estado do
  // áudio: o navegador pode suspender o contexto sozinho (perda de foco de
  // áudio do sistema, hibernação da aba) sem avisar a aplicação, e o indicador
  // não pode continuar dizendo "ativo" enquanto o som está morto.
  useEffect(() => {
    const id = setInterval(() => {
      setMachine((m) => tick(m, Date.now()));
      setAudioStatus((anterior) => {
        const atual = audio.status();
        return atual === anterior ? anterior : atual;
      });
    }, TICK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [audio]);

  useEffect(() => {
    // Sem sessão não há a quem atribuir o retrato. O provider vive no shell e
    // já está montado na tela de login; gravar ali criaria uma chave órfã.
    if (!token) return;
    saveSnapshot(scopeRef.current, machine.snapshot);
  }, [machine.snapshot, token]);

  const hasNotices = machine.notices.length > 0;

  // O som acompanha a existência de aviso: começa no primeiro e só para quando
  // o último se encerra, seja por ação do operador ou por normalização.
  useEffect(() => {
    if (hasNotices) {
      audio.start();
    } else {
      audio.stop();
    }
    setAudioStatus(audio.status());
  }, [hasNotices, audio]);

  useEffect(() => () => audio.dispose(), [audio]);

  // Reconciliação do reconhecimento feito em OUTRA sessão.
  //
  // O reconhecimento não se propaga em tempo real; sem isto, duas estações
  // olhando o mesmo ativo apitariam até cada uma reconhecer por conta própria.
  // Enquanto houver aviso ativo, a lista de alarmes é consultada em intervalo
  // fixo, e o aviso cujo alarme foi reconhecido por qualquer sessão se encerra
  // aqui também — como se o reconhecimento tivesse sido local.
  //
  // A régua de encerramento é a mesma do reconhecimento local, e conservadora
  // nas duas direções: só encerra com prova POSITIVA de reconhecimento e
  // NENHUMA pendência aberta do mesmo ativo. Ativo que não aparece na lista
  // fica como está — pode ser atraso do back-end, e errar mantendo o alerta é
  // o lado certo do erro. Falha na consulta idem: nada muda.
  useEffect(() => {
    if (!token || !hasNotices) return undefined;
    let cancelado = false;
    let emCurso = false;

    const conferir = async () => {
      // O serviço de API não define timeout: um back-end lento não pode
      // empilhar uma consulta por tick.
      if (emCurso) return;
      emCurso = true;
      try {
        const resposta = await fetchRef.current("/alarms");
        if (cancelado) return;
        const alarmes = resposta?.data;
        if (!Array.isArray(alarmes)) return;

        const pendentes = new Set();
        const reconhecidos = new Set();
        alarmes.forEach((a) => {
          if (!a || a.end_time || !a.device?.label) return;
          (a.recognized ? reconhecidos : pendentes).add(a.device.label);
        });

        setMachine((m) =>
          m.notices
            .filter(
              (n) => reconhecidos.has(n.assetId) && !pendentes.has(n.assetId),
            )
            .reduce((acc, n) => dismiss(acc, n.assetId), m),
        );
      } catch {
        // Falha de rede não encerra nem cala nada: o aviso permanece.
      } finally {
        emCurso = false;
      }
    };

    const id = setInterval(conferir, RECOGNITION_POLL_MS);
    return () => {
      cancelado = true;
      clearInterval(id);
    };
  }, [token, hasNotices]);

  const enableAudio = useCallback(async () => {
    await audio.enable();
    if (machine.notices.length > 0) audio.start();
    const status = audio.status();
    setAudioStatus(status);
    return status;
  }, [audio, machine.notices.length]);

  const toggleMuted = useCallback(() => {
    const mudo = !audio.isMuted();
    setAudioStatus(audio.setMuted(mudo));
    saveAudioPreference(scopeRef.current, { muted: mudo });
  }, [audio]);

  const changeVolume = useCallback(
    (value) => {
      setAudioStatus(audio.setVolume(value));
      const atual = audio.getVolume();
      setAudioVolume(atual);
      saveAudioPreference(scopeRef.current, { volume: atual });
    },
    [audio],
  );

  // Preferência do operador entre sessões.
  //
  // Volume e silenciamento são estado puro: valem de imediato, sem gesto.
  //
  // "Som habilitado" NÃO é restaurável, e também não é inferido. A política de
  // autoplay exige gesto a cada carregamento, e houve uma tentativa de aceitar
  // qualquer clique da página como esse gesto: ela funcionava, mas ligava o som
  // enquanto o operador fazia outra coisa, trocando o conteúdo do aviso sem que
  // ele tivesse pedido. Quem liga o som é o botão, e só ele. O aviso abre
  // sozinho a cada carga justamente para que esse botão esteja à mão.
  useEffect(() => {
    if (!token) return;
    const pref = loadAudioPreference(scopeRef.current);
    if (pref.volume !== null) {
      audio.setVolume(pref.volume);
      setAudioVolume(audio.getVolume());
    }
    if (pref.muted) audio.setMuted(true);
    setAudioStatus(audio.status());
  }, [audio, token]);

  // Recolher é apresentação, não encerramento. O aviso continua na máquina, o
  // som continua tocando e o ativo continua contado — o operador apenas
  // desocupou a tela. Encerrar aqui permitiria calar um crítico que ninguém
  // reconheceu, que é exatamente o que a feature existe para impedir.
  const minimizeNotice = useCallback((assetId) => {
    setMinimizados((atuais) =>
      atuais.includes(assetId) ? atuais : [...atuais, assetId],
    );
  }, []);

  const expandNotices = useCallback(() => setMinimizados([]), []);

  // Aviso que se encerrou tem de sair da lista de recolhidos. Sem isto, um
  // ativo que normalizasse e voltasse a ficar crítico nasceria já recolhido —
  // um alarme novo invisível, pelo estado de um alarme antigo.
  useEffect(() => {
    setMinimizados((atuais) => {
      const vivos = atuais.filter((assetId) =>
        machine.notices.some((n) => n.assetId === assetId),
      );
      return vivos.length === atuais.length ? atuais : vivos;
    });
  }, [machine.notices]);

  const acknowledgeNotice = useCallback(
    (assetId) => setMachine((m) => dismiss(m, assetId)),
    [],
  );

  const value = useMemo(() => {
    const describe = (notice) => ({
      ...notice,
      device: devices[notice.assetId] || null,
      name: devices[notice.assetId]?.name || notice.assetId,
    });

    // Recolher libera a vaga: quem estava na fila entra no lugar. O teto de
    // avisos visíveis continua valendo, e nenhum aviso é descartado.
    const visiveis = machine.notices.filter(
      (n) => !minimizados.includes(n.assetId),
    );

    return {
      notices: visiveis.slice(0, MAX_VISIBLE_NOTICES).map(describe),
      queuedCount: Math.max(0, visiveis.length - MAX_VISIBLE_NOTICES),
      minimizedCount: machine.notices.length - visiveis.length,
      totalCount: machine.notices.length,
      audioStatus,
      audioVolume,
      channelConnected: connected,
      contextResolved,
      snapshotSeeded,
      enableAudio,
      toggleMuted,
      changeVolume,
      minimizeNotice,
      expandNotices,
      acknowledgeNotice,
    };
  }, [
    machine.notices,
    minimizados,
    devices,
    audioStatus,
    audioVolume,
    connected,
    contextResolved,
    snapshotSeeded,
    enableAudio,
    toggleMuted,
    changeVolume,
    minimizeNotice,
    expandNotices,
    acknowledgeNotice,
  ]);

  return (
    <AlarmNotificationContext.Provider value={value}>
      {children}
    </AlarmNotificationContext.Provider>
  );
};

AlarmNotificationProvider.propTypes = {
  children: PropTypes.node,
  audioFactory: PropTypes.func,
};
